import { useRef, useState } from "react";
import { TESLIM_ALANLARI, hedefEsit, teslimEtiketi, type GelenHedef, type TeslimKartKalemi, type TeslimKarti } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  OKUMA_UCU,
  TESLIM_UCU,
  okumaDugmesi,
  okumaEylemleri,
  teslimBugun,
  teslimDurumMetni,
  teslimEkleyen,
  teslimFormu,
  teslimKarsilastirmaSatirlari,
  teslimMiktarMetni,
  teslimNedenMetni,
  teslimOneriMetni,
  teslimPaketAcilabilir,
  teslimSayfalari,
  teslimYenidenMetni,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarih, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 4 · PR 20: Documents kartının packing list "Document reading" bölümü (prototip belgeler--teslim-okuma, kart 1-4; `KalibrasyonOkumaBolumu` iskeleti). Okuma yalnız
 * eşlemenin, kuralların, "Use the reading"in, paket açmanın ve liste eklemenin kullandığı değerleri gösterir (sözleşme §12): başlıkta 3, satır başına 5 değer. Rev, SO, alıcı,
 * şartlar, tanım, tarih kodu, ambalaj satırları ve imzalar okunmaz. Aynı PN + lot satırları tek satır (kalem) sayılır; OASIS her kalemin stok paketini PN ve lot ya da SN ile
 * birebir önerir. "Use this place" kartın bugünkü tür+yer kaydını çağırır; "Use the reading" yalnız dosyalama formunu doldurur (sunucuya yazmaz). Paketi olmayan kalem için yeni
 * paket yalnız PM ya da sistem yöneticisinin "Open stock package" onayıyla, geliş tarihini kişi yazarak açılır; dosyalamadan sonra liste öteki paketlere "Add the list" ile tek
 * tek eklenir. `salt` kapanmış satır içindir: form ve karar düğmeleri yok; "Open stock package" ve "Add the list" sunucunun eylemiyle çizilir. Okuma excel'inin adı kartta yoktur.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";
const ISO_GUN = /^\d{4}-\d{2}-\d{2}$/;

const aralik = (s: { bas: number; son: number }) => (s.bas === s.son ? String(s.bas) : `${s.bas}-${s.son}`);
const durumTonu = (d: "kisi" | "oneri" | "yok") => (d === "kisi" ? "ok" : d === "oneri" ? "info" : "neutral");

export interface TeslimFormYardimi {
  /** "Use the reading": dosyalama formunun alanları (yalnız kesin liste no) */
  kullan: (alanlar: Record<string, string>) => void;
  /** "Use the reading": Pages alanı (liste PDF'in bir kısmıysa) */
  sayfalar?: (s: string) => void;
  /** "Use this place": bugünkü tür+yer kaydı */
  yerKaydet: (h: GelenHedef) => Promise<void>;
}

export function TeslimOkumaBolumu({ satirId, k, onYenile, salt, hedef, form }: { satirId: string; k: TeslimKarti; onYenile: () => void; salt?: boolean; hedef: GelenHedef | null; form?: TeslimFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const [gunler, setGunler] = useState<Record<number, string>>({});
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  const karsilastirmaSatirlari = teslimKarsilastirmaSatirlari(k);
  const yeniden = teslimYenidenMetni(k.yenidenKarsilastirma);
  const toplamUyari = (k.karsilastirma?.uyarilar.length ?? 0) + k.kalemler.reduce((t, c) => t + c.uyarilar.length, 0);
  const kirmiziKalem = k.kalemler.find((c) => c.uyarilar.some((u) => u.renk === "kirmizi" && !u.karar));
  const hedefPaket = hedef?.tur === "kutuk" && hedef.kutuk === "stok_paket" ? k.kalemler.find((c) => hedefEsit(c.yer.yer, hedef))?.yer.paket ?? null : null;
  const sayfalar = teslimSayfalari(k);

  const satirHucresi = (c: TeslimKartKalemi) => (
    <>
      <span className="font-mono">{c.satirlar.length > 1 ? cevir("{0} · p.{1} (lines {2})", c.sira, c.sayfa ?? "—", c.satirlar.join(", ")) : cevir("{0} · p.{1}", c.sira, c.sayfa ?? "—")}</span>
    </>
  );
  const listeHucresi = (c: TeslimKartKalemi) => {
    const suphe = c.degerler.some((d) => d.kesinlik === "suphe");
    return (
      <span className="inline-flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1.5"><Nokta k={suphe ? "suphe" : "kesin"} /><span>{[c.pn ?? "—", teslimMiktarMetni(c), c.kimlikMetni].filter(Boolean).join(" · ")}</span>{sayfaLinki(c.sayfa)}</span>
        {c.cevrilmis && <span className="text-xs text-ink-3">{cevir("= {0} (unit of the stock item)", c.cevrilmis)}</span>}
      </span>
    );
  };
  const paketHucresi = (c: TeslimKartKalemi) => {
    const p = c.yer.paket;
    const acabilir = teslimPaketAcilabilir(c, k);
    const gun = gunler[c.sira] ?? "";
    const gunGecerli = ISO_GUN.test(gun) && gun <= teslimBugun();
    return (
      <>
        {p && c.yer.durum !== "yok" && (
          <>
            <span className="mr-1.5">{[p.pkg, p.iz, `${p.gelenMiktar} ${p.birim}`].filter(Boolean).join(" · ")}</span>
            <Etiket tone={durumTonu(c.yer.durum)}>{c.yer.durum === "kisi" ? cevir("your choice") : cevir("proposed")}</Etiket>
            {p.kaynak === "teslim_listesi" && p.kaynakRef === satirId && c.onay && (
              <span className="block text-xs text-ink-3">{cevir("opened from this reading by {0}, {1} · arrived {2}", c.onay.kim, tarihSaat(c.onay.ts), p.gelisTarihi ? tarih(p.gelisTarihi) : "—")}</span>
            )}
          </>
        )}
        {(!p || c.yer.durum === "yok") && <span className="text-ink-2">{teslimNedenMetni(c.yer.neden)}</span>}
        {yardim && c.yer.durum === "oneri" && c.yer.yer && !hedefEsit(c.yer.yer, hedef) && (
          <Buton kucuk className="ml-1 mt-1" disabled={mesgul !== null} mesgul={mesgul === `yer-${c.sira}`} onClick={() => calistir(`yer-${c.sira}`, () => yardim.yerKaydet(c.yer.yer!))}>{cevir("Use this place")}</Buton>
        )}
        {c.oneri && (c.durum === "acilabilir" || c.durum === "acildi") && (
          <span className="mt-1 block text-xs text-ink-2">{teslimOneriMetni(c.oneri)}</span>
        )}
        {c.oneri && c.durum === "acilabilir" && (
          <span className="mt-1 block">
            {c.oneri.ipucuTarih && <span className="block text-xs text-ink-3">{cevir("Shipped {0} (list date); the arrival date is typed, not read.", tarih(c.oneri.ipucuTarih))}</span>}
            {acabilir
              ? (
                <span className="mt-1 flex flex-wrap items-center gap-1.5">
                  <label className="flex items-center gap-1 text-xs">
                    <span className="text-ink-2">{cevir("Arrival date")}</span>
                    <input
                      className="rounded-md border border-line bg-surface px-1.5 py-0.5 text-xs" type="date" max={teslimBugun()} value={gun} disabled={mesgul !== null}
                      onChange={(e) => setGunler({ ...gunler, [c.sira]: e.target.value })}
                    />
                  </label>
                  <Buton kucuk disabled={mesgul !== null || !gunGecerli || Boolean(c.engel)} mesgul={mesgul === `paket-${c.sira}`}
                    onClick={() => calistir(`paket-${c.sira}`, () => api.post(TESLIM_UCU.paket(satirId), { sira: c.sira, gelisTarihi: gun }))}>
                    {cevir("Open stock package")}
                  </Buton>
                </span>
              )
              : <span className="block text-xs text-ink-3">{cevir("A PM or System administrator opens the package.")}</span>}
          </span>
        )}
      </>
    );
  };
  const sonucHucresi = (c: TeslimKartKalemi) => {
    const ekleyen = teslimEkleyen(c);
    return (
    <>
      {c.durum === "eklenebilir" && k.eylemler.listeEkle
        ? (
          <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === `liste-${c.sira}`} onClick={() => calistir(`liste-${c.sira}`, () => api.post(TESLIM_UCU.liste(satirId), { sira: c.sira }))}>
            {cevir("Add the list")}
          </Buton>
        )
        : teslimDurumMetni(c, hedef)}
      {ekleyen && <span className="block text-xs text-ink-3">{cevir("Added by {0} on {1}", ekleyen.kim, tarihSaat(ekleyen.ts))}</span>}
    </>
    );
  };

  return (
    <div data-bolum="teslim-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the packing list for you to check. Nothing is filed or changed.")}</p>}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[
              o.sayfalar ? cevir("Packing list p.{0}", aralik(o.sayfalar)) : null,
              k.kod ? cevir("Reading job {0}", k.kod) : null,
              cevir("{0} lines read, {1} after joining the same part number and lot", o.satirSayisi, o.kalemSayisi),
            ].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          <ul className="mt-2 space-y-0.5 text-sm" data-bolum="teslim-baslik">
            {TESLIM_ALANLARI.map((a) => {
              const d = k.degerler.find((x) => x.alan === a.alan);
              const deger = a.alan === "liste_tarihi" && d?.deger && ISO_GUN.test(d.deger) ? tarih(d.deger) : d?.deger;
              return (
                <li key={a.alan} className="flex gap-2">
                  <Nokta k={d?.kesinlik ?? "bos"} />
                  <span className="w-[10rem] shrink-0 text-ink-2">{cevir(teslimEtiketi(a.alan))}</span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                    {sayfaLinki(d?.sayfa ?? null)}
                    {d?.yazildigi && d.yazildigi !== d.deger && <span className="block text-xs text-ink-3">{cevir("as written: {0}", d.yazildigi)}</span>}
                  </span>
                </li>
              );
            })}
          </ul>

          <AltBaslik>{cevir("Lines of the list")}</AltBaslik>
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" data-bolum="bl-kars">
              <thead><tr className="border-b border-line">
                <th className={TABLO_BASI}>{cevir("Line")}</th>
                <th className={TABLO_BASI}>{cevir("On the list")}</th>
                <th className={TABLO_BASI}>{cevir("Stock package")}</th>
                <th className={TABLO_BASI}>{cevir("Result")}</th>
              </tr></thead>
              <tbody className="divide-y divide-line">
                {k.kalemler.map((c) => (
                  <tr key={c.sira} data-kalem={c.sira}>
                    <td className={TABLO_HUCRESI}>{satirHucresi(c)}</td>
                    <td className={TABLO_HUCRESI}>{listeHucresi(c)}</td>
                    <td className={TABLO_HUCRESI}>{paketHucresi(c)}</td>
                    <td className={TABLO_HUCRESI}>{sonucHucresi(c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-1 text-xs text-ink-3">{cevir("OASIS proposes the package with the same part number and the same lot or serial number. A line without a package can get a new package; nothing is opened by itself.")}</p>
          {(k.eylemler.paketAc || k.eylemler.listeEkle || k.kalemler.some((c) => c.durum === "acilabilir" || c.durum === "dosyalanmadi")) && (
            <p className="mt-1 text-xs text-ink-3">
              {cevir("“Add the list” files this PDF on the package of that line too (Delivery note / packing list, Doc no. {0}). “Open stock package” adds a new package on Registers › Stock with the part number, the quantity in the unit of the stock item, the lot and serial numbers as heat / lot / SN and the customer PO of the list; you type the arrival date. A package or a document already in Registers does not change. Both are one line at a time; nothing is undone from this card (a PM or System administrator edits or deletes the package on Registers).", teslimFormu(k).belgeNo || "—")}
            </p>
          )}

          {yardim && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-bolum="okumayi-kullan">
              <Buton kucuk disabled={mesgul !== null} onClick={() => {
                yardim.kullan(teslimFormu(k));
                if (sayfalar && yardim.sayfalar) yardim.sayfalar(sayfalar);
              }}>{cevir("Use the reading")}</Buton>
              <span className="w-full text-xs text-ink-3">
                {cevir("“Use the reading” fills Doc no. with the certain list number and Pages with the pages of the list when it is part of the PDF; an uncertain or empty value stays empty for you to type. Nothing is filed until you confirm.")}
              </span>
            </div>
          )}
          {yardim && kirmiziKalem && hedefPaket && !hedefEsit(kirmiziKalem.yer.yer, hedef) && (
            <Band tip="not" className="mt-2">
              {cevir("The red alert of line {0} does not stop filing the list on {1}: the filing gate looks at the alerts of the line of that package.", kirmiziKalem.sira, hedefPaket.pkg)}
            </Band>
          )}
          <p className="mt-1 text-xs text-ink-3">
            {cevir("Not read: revision, sales order and its line, description, consignee, delivery terms, date code, packing lines (skid, crate, pallet), “Packed by” and carrier signatures, and the terms page. Lines with the same part number and lot are counted as one line.")}
          </p>
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {(karsilastirmaSatirlari.length > 0 || yeniden) && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {karsilastirmaSatirlari.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.karsilastirildi ? "kesin" : n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
              {yeniden && (
                <li className="flex gap-2">
                  <Nokta k="kesin" />
                  <span className="min-w-0">{yeniden}</span>
                </li>
              )}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && toplamUyari === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : toplamUyari === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <div className="space-y-2">
                  {k.kalemler.filter((c) => c.uyarilar.length > 0).map((c) => (
                    <div key={c.sira} data-kalem-uyari={c.sira}>
                      <p className="text-xs font-medium text-ink-2">{cevir("Line {0}: {1}", c.sira, c.pn ?? "—")}</p>
                      <ul className="mt-0.5 space-y-1.5">
                        {c.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                      </ul>
                    </div>
                  ))}
                  {k.karsilastirma.uyarilar.length > 0 && (
                    <ul className="space-y-1.5">
                      {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                    </ul>
                  )}
                </div>
              )}
          {k.kalemler.some((c) => c.uyarilar.some((u) => u.kod === "TES-02" || u.kod === "TES-03" || u.kod === "TES-05")) && (
            <p className="mt-1 text-xs text-ink-3">{cevir("The decision is a record of its own: the package, its PO and its quantity do not change. A PM or System administrator edits the package on Registers if the list is right.")}</p>
          )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(TESLIM_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(TESLIM_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(TESLIM_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
